import React, { useState } from 'react';
import { ChatMessage as ChatMessageType, ChatSource } from '../../contexts/ChatContext';
import ThinkingBlock from './ThinkingBlock';

interface ChatMessageProps {
  message: ChatMessageType;
  showThinking: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, showThinking }) => {
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);

  const getSourceStyles = (source: ChatSource) => {
    switch (source) {
      case 'claude_chat':
        return {
          borderColor: '#8b5cf6',
          iconBg: 'rgba(139, 92, 246, 0.1)',
        };
      case 'claude_code':
        return {
          borderColor: '#10b981',
          iconBg: 'rgba(16, 185, 129, 0.1)',
        };
      case 'system':
        return {
          borderColor: '#6b7280',
          iconBg: 'rgba(107, 114, 128, 0.1)',
        };
    }
  };

  const getSourceIcon = (source: ChatSource) => {
    switch (source) {
      case 'claude_chat':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        );
      case 'claude_code':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        );
      case 'system':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        );
    }
  };

  const styles = getSourceStyles(message.source);
  const isUser = message.role === 'user';

  return (
    <div
      className={`chat-message ${message.role}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '0.75rem 1rem',
        marginBottom: '0.75rem',
        borderRadius: '12px',
        background: isUser ? 'var(--bg-tertiary)' : 'transparent',
        borderLeft: isUser ? 'none' : `3px solid ${styles.borderColor}`,
      }}
    >
      {/* Header with source icon */}
      {!isUser && (
        <div
          className="message-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: styles.iconBg,
            }}
          >
            {getSourceIcon(message.source)}
          </span>
          <span style={{ textTransform: 'capitalize' }}>
            {message.source.replace('_', ' ')}
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>
            {message.timestamp.toLocaleTimeString()}
          </span>
        </div>
      )}

      {/* Thinking block */}
      {showThinking && message.thinking && (
        <ThinkingBlock
          content={message.thinking}
          isExpanded={isThinkingExpanded}
          onToggle={() => setIsThinkingExpanded(!isThinkingExpanded)}
        />
      )}

      {/* Message content */}
      <div
        className="message-content"
        style={{
          fontSize: '0.875rem',
          lineHeight: '1.5',
          color: 'var(--text-primary)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {message.content}
      </div>

      {/* User message timestamp */}
      {isUser && (
        <div
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-tertiary)',
            textAlign: 'right',
          }}
        >
          {message.timestamp.toLocaleTimeString()}
        </div>
      )}

      {/* Loading indicator */}
      {message.isLoading && (
        <div
          className="loading-dots"
          style={{
            display: 'flex',
            gap: '4px',
            paddingTop: '0.25rem',
          }}
        >
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: 'var(--text-secondary)',
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: 'var(--text-secondary)',
              animation: 'pulse 1.4s ease-in-out 0.2s infinite',
            }}
          />
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: 'var(--text-secondary)',
              animation: 'pulse 1.4s ease-in-out 0.4s infinite',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
